import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import styled from 'styled-components';
import Layout from '../components/Layout';
import MenuItemCard from '../components/MenuItemCard';
import CartSummary from '../components/CartSummary';
import {restaurantApi} from '../services/api';
import {useMerchant} from '../context/MerchantContext';
import {Category} from '../types';
import {collectVisiblePromotions, getPromotionSummary, getPromotionTypeLabel} from '../utils/promotionUtils';

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const PageHeader = styled.div`
  text-align: center;
  margin-bottom: 2rem;
`;

const PageTitle = styled.h1`
  font-size: 2.5rem;
  color: #333;
  margin-bottom: 0.5rem;
`;

const PageDescription = styled.p`
  font-size: 1.2rem;
  color: #666;
  max-width: 800px;
  margin: 0 auto;
`;


const CategorySection = styled.section`
  margin-bottom: 3rem;
`;

const CategoryHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  border-bottom: 3px solid #e74c3c;
  padding-bottom: 0.8rem;
  position: relative;
`;

const CategoryTitle = styled.h2`
  font-size: 2rem;
  color: #333;
  font-weight: 700;
  text-transform: uppercase;
  position: relative;
  padding-left: 0.5rem;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 5px;
    height: 70%;
    background-color: #e74c3c;
    border-radius: 2px;
  }
`;

const ItemsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
    gap: 1.5rem;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const CategoryPromotionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const CategoryPromotionCard = styled.article<{ $themeColor: string }>`
  border-radius: 16px;
  border: 1px solid ${props => `${props.$themeColor}22`};
  background: linear-gradient(135deg, ${props => `${props.$themeColor}10`} 0%, #ffffff 65%);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.05);
  padding: 1rem;
`;

const CategoryPromotionTitle = styled.h3`
  font-size: 1rem;
  font-weight: 800;
  color: #1f2937;
  margin-bottom: 0.5rem;
`;

const CategoryPromotionMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  color: #4b5563;
  font-size: 0.82rem;
`;

const CategoryPromotionChip = styled.span<{ $themeColor: string }>`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.3rem 0.55rem;
  border-radius: 999px;
  background: ${props => `${props.$themeColor}16`};
  color: ${props => props.$themeColor};
  font-size: 0.72rem;
  font-weight: 800;
`;

const SkeletonBlock = styled.div<{ width?: string; height?: string; $themeColor?: string }>`
  width: ${props => props.width || '100%'};
  height: ${props => props.height || '1rem'};
  border-radius: 8px;
  background: linear-gradient(
    90deg,
    #edf2f7 20%,
    ${props => `${props.$themeColor || '#e74c3c'}1A`} 38%,
    #edf2f7 62%
  );
  background-size: 400% 100%;
  animation: promotions-skeleton-loading 1.4s ease infinite;

  @keyframes promotions-skeleton-loading {
    0% { background-position: 100% 50%; }
    100% { background-position: 0 50%; }
  }
`;

const PromotionSkeletonCard = styled.div`
  min-height: 170px;
  border-radius: 12px;
  background: #fff;
  border: 1px solid #edf2f7;
  padding: 1rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 300px;
  color: #e74c3c;
  text-align: center;
`;

const ErrorTitle = styled.h2`
  font-size: 1.8rem;
  margin-bottom: 1rem;
`;

const ErrorMessage = styled.p`
  font-size: 1.2rem;
  color: #666;
  max-width: 600px;
`;

const RetryButton = styled.button`
  margin-top: 1.5rem;
  padding: 0.75rem 1.5rem;
  background-color: #e74c3c;
  color: white;
  border: none;
  border-radius: 4px;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    background-color: #c0392b;
  }
`;

const PromotionsPage: React.FC = () => {
  const { restaurantId: paramId } = useParams<{ restaurantId?: string }>();
  const { 
    merchantId: contextId, 
    restaurant: merchantRestaurant, 
    isLoading: isMerchantLoading,
    setMenuCategories 
  } = useMerchant();
  const restaurantId = paramId || contextId;
  const restaurant = merchantRestaurant;
  const themeColor = restaurant?.theme?.primary || '#e74c3c';
  
  const navigate = useNavigate();
  const [promotionalCategories, setPromotionalCategories] = useState<Category[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const visiblePromotions = useMemo(() => collectVisiblePromotions(promotionalCategories), [promotionalCategories]);

  // Function to fetch menu data
  const fetchMenu = useCallback(async () => {
    if (!restaurantId) return;

    setIsLoading(true);
    setError(null);
    try {
      const data = await restaurantApi.getMenu(restaurantId);
      setMenuCategories(data);

      // Include categories that have either promo metadata or discounted items
      const promotionalCats = data.filter(category => (
        (category.promos || []).some(promo => promo.active && promo.showInMenu !== false) ||
        category.items.some(item => item.originalPrice !== undefined || item.comercial?.promotionActive)
      ));
      
      setPromotionalCategories(promotionalCats);
      setIsLoading(false);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('An unknown error occurred'));
      setIsLoading(false);
    }
  }, [restaurantId, setMenuCategories]);

  // Fetch data on component mount or when restaurantId changes
  useEffect(() => {
    fetchMenu();
  }, [fetchMenu]);

  const renderPromotionsSkeleton = () => (
    <Layout>
      <PageContainer>
        <PageHeader>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '0.75rem' }}>
            <SkeletonBlock width="220px" height="2.25rem" $themeColor={themeColor} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <SkeletonBlock width="min(100%, 520px)" height="1rem" $themeColor={themeColor} />
          </div>
        </PageHeader>
        <div className="row g-4">
          <div className="col-12 col-lg-8">
            <CategorySection>
              <CategoryHeader>
                <SkeletonBlock width="180px" height="1.6rem" $themeColor={themeColor} />
              </CategoryHeader>
              <ItemsGrid>
                {Array.from({ length: 6 }).map((_, index) => (
                  <PromotionSkeletonCard key={index}>
                    <SkeletonBlock width="62%" height="1rem" $themeColor={themeColor} />
                    <div style={{ marginTop: '0.75rem' }}>
                      <SkeletonBlock width="92%" height="0.8rem" $themeColor={themeColor} />
                    </div>
                    <div style={{ marginTop: '0.5rem' }}>
                      <SkeletonBlock width="70%" height="0.8rem" $themeColor={themeColor} />
                    </div>
                    <div style={{ marginTop: '1.75rem' }}>
                      <SkeletonBlock width="90px" height="1.2rem" $themeColor={themeColor} />
                    </div>
                  </PromotionSkeletonCard>
                ))}
              </ItemsGrid>
            </CategorySection>
          </div>
        </div>
      </PageContainer>
    </Layout>
  );

  if (isLoading || isMerchantLoading) {
    return renderPromotionsSkeleton();
  }

  if (error) {
    return (
      <Layout>
        <ErrorContainer>
          <ErrorTitle>Ops! Algo deu errado.</ErrorTitle>
          <ErrorMessage>
            Não foi possível carregar as promoções. Por favor, tente novamente mais tarde.
          </ErrorMessage>
          <RetryButton onClick={() => fetchMenu()}>
            Tentar Novamente
          </RetryButton>
        </ErrorContainer>
      </Layout>
    );
  }

  if (promotionalCategories.length === 0) {
    return (
      <Layout>
        <ErrorContainer>
          <ErrorTitle>Nenhuma promoção disponível</ErrorTitle>
          <ErrorMessage>
            No momento não temos promoções disponíveis. Volte em breve para conferir nossas ofertas!
          </ErrorMessage>
          <RetryButton onClick={() => navigate('/')}>
            Voltar ao Cardápio
          </RetryButton>
        </ErrorContainer>
      </Layout>
    );
  }

  return (
    <Layout>
      <PageContainer>
        <PageHeader>
          <PageTitle>Promoções</PageTitle>
          <PageDescription>
            Confira nossas ofertas especiais e aproveite para economizar!
          </PageDescription>
          {visiblePromotions.length > 0 && (
            <div className="d-flex justify-content-center mt-3">
              <span className="badge rounded-pill bg-light text-dark border px-3 py-2">
                {visiblePromotions.length} promoção(ões) ativas no cardápio
              </span>
            </div>
          )}
        </PageHeader>

        <div className="row g-4">
          <div className="col-12 col-lg-8">
            {promotionalCategories.map((category) => (
              <CategorySection key={category.id}>
                <CategoryHeader>
                  <CategoryTitle>{category.name}</CategoryTitle>
                </CategoryHeader>
                {(category.promos || []).filter(promo => promo.active && promo.showInMenu !== false).length > 0 && (
                  <CategoryPromotionsGrid>
                    {(category.promos || [])
                      .filter(promo => promo.active && promo.showInMenu !== false)
                      .slice(0, 4)
                      .map(promo => (
                        <CategoryPromotionCard key={promo.id} $themeColor={themeColor}>
                          <div className="d-flex align-items-center justify-content-between gap-2 mb-2">
                            <CategoryPromotionChip $themeColor={themeColor}>
                              <i className="fas fa-bolt" />
                              {getPromotionTypeLabel(promo.type)}
                            </CategoryPromotionChip>
                            {promo.allowWithCoupon && (
                              <span className="small text-muted">Com cupom</span>
                            )}
                          </div>
                          <CategoryPromotionTitle>{promo.name}</CategoryPromotionTitle>
                          <p className="small text-muted mb-2">
                            {promo.description || 'Oferta ativa e visível para o cliente no cardápio.'}
                          </p>
                          <CategoryPromotionMeta>
                            <span>
                              <i className="fas fa-calendar-alt me-2" style={{ color: themeColor }} />
                              {getPromotionSummary(promo)}
                            </span>
                            <span>
                              <i className="fas fa-layer-group me-2" style={{ color: themeColor }} />
                              {promo.categories?.length > 0
                                ? promo.categories.map(cat => cat.name).join(' • ')
                                : `${promo.categoryIds?.length || 0} categoria(s)`}
                            </span>
                          </CategoryPromotionMeta>
                        </CategoryPromotionCard>
                      ))}
                  </CategoryPromotionsGrid>
                )}
                <ItemsGrid>
                  {category.items.map((item) => (
                    <MenuItemCard 
                      key={item.id} 
                      item={item} 
                      stockControlEnabled={restaurant?.stockControlEnabled}
                    />
                  ))}
                </ItemsGrid>
              </CategorySection>
            ))}
          </div>

          <div className="col-12 col-lg-4 d-none d-lg-block">
            <div className="sticky-top" style={{ top: '6rem', zIndex: 10 }}>
              <CartSummary />
            </div>
          </div>
        </div>
      </PageContainer>
    </Layout>
  );
};

export default PromotionsPage;
